import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import StartStep from "./productSelector/components/StartStep";
import SegmentStep from "./productSelector/components/SegmentStep";
import { AppContentWrapper } from "framework/components/AppContentWrapper";
import ApplicationStep from "./productSelector/components/ApplicationStep";
import { FooterNavigationButtons } from "./productSelector/components/common/FooterNavigationButtons";
import FeaturesStep from "./productSelector/components/FeaturesStep";
import ComparisonStep from "./productSelector/components/ComparisonStep";
import RecommendedStep from "./productSelector/components/RecommendedStep";
import Stepper, { Step } from "framework/components/Stepper";
import { Box, Stack } from "@mui/material";
import { Heading } from "@abb/abb-common-ux-react";
import { SelectorMainApplicationDto, SelectorSegmentDto } from "api";
import { getApiRegistry } from "framework/state/apiRegistry";
import { SubSegment } from "./productSelector/components/common/SubSegment";
import { MainApplication } from "./productSelector/components/common/MainApplication";

interface ProductsSelection {
    segment?: SelectorSegmentDto,
    application?: SelectorMainApplicationDto
}

const ProductsView = (): JSX.Element => {

    const [activeStep, setActiveStep] = useState(0);
    const [productSelection, setProductSelection] = useState<ProductsSelection>({ segment: undefined } as ProductsSelection);

    const [segments, setSegments] = useState<SelectorSegmentDto[]>([]);
    const [subSegments, setSubSegments] = useState<SubSegment[]>([]);
    const [applications, setApplications] = useState<MainApplication[]>([]);

    const { t } = useTranslation();

    useEffect(() => {
        getApiRegistry().selectorApi.apiSelectorSegmentsGet().then((segments) => {
            setSegments(segments);
            loadAllSegmentPictograms(segments);
        });

        getApiRegistry().selectorApi.apiSelectorApplicationsGet().then((applications) => {
            setApplications(applications);
            loadAllApplicationPictograms(applications);
        });

    }, []);

    const loadAllSegmentPictograms = (segments: SelectorSegmentDto[]) => {
        const allSubSegments: SubSegment[] = [];
        const allPromises: Promise<void | Blob>[] = [];
        segments.forEach(s => {
            s.selectorSubSegments?.forEach(sub => {
                if (sub.pictogram) {
                    allPromises.push(
                        getApiRegistry().selectorApi.apiSelectorImagesImageNameGet({ imageName: sub.pictogram }).then((file) => {
                            const reader = new FileReader();
                            reader.readAsDataURL(file);
                            reader.onloadend = function () {
                                allSubSegments.push({ ...sub, pictogramUrl: reader.result ? reader.result.toString() : '' });
                            }
                        })
                    );
                }
            });
        });

        Promise.all(allPromises).then(() => {
            setTimeout(() => {
                setSubSegments(allSubSegments);
            }, 10);
        });
    }

    const loadAllApplicationPictograms = (applications: MainApplication[]) => {
        const allApplication: MainApplication[] = [];
        const allPromises: Promise<void | Blob>[] = [];
        applications.forEach(s => {
            if (s.pictogram) {
                allPromises.push(
                    getApiRegistry().selectorApi.apiSelectorImagesImageNameGet({ imageName: s.pictogram }).then((file) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onloadend = function () {
                            s.pictogramUrl = reader.result ? reader.result.toString() : ''
                        }
                    })
                );
            }
        });

        Promise.all(allPromises).then(() => {
            setTimeout(() => {
                setApplications(applications);
            }, 10);
        });
    }

    const onNextClick = () => {
        setActiveStep(activeStep + 1);
    }

    const onPrevClick = () => {
        setActiveStep(activeStep - 1);
    }

    const withFooter = (tab: JSX.Element) => {
        return <>{tab} <FooterNavigationButtons onPrevClick={onPrevClick} step={activeStep} onNextClick={onNextClick} ></FooterNavigationButtons></>
    }

    const onStepChanged = (step: number) => {
        console.log(step)
        //if (completedStep + 1 >= step)
        setActiveStep(step);
    }

    const onSegmentSelected = (segment?: SelectorSegmentDto) => {
        setProductSelection(ps => ({ ...ps, segment: segment }))
    }

    const onApplicationSelected = (application?: SelectorMainApplicationDto) => {
        setProductSelection(ps => ({ ...ps, application: application }))
    }

    const steps = useMemo(() => {
        return [
            t("1. Start"),
            productSelection.segment ? `2. ${productSelection.segment.name}` : t("2. Segment"),
            productSelection.application ? `3. ${productSelection.application.name}` : t("3. Application"),
            t("4. Features"), t("5. Comparison"), t("6. Recommendation")];
    }, [productSelection])

    return <AppContentWrapper isMainView={true} titleHeadingLevel={3} >
        {activeStep === 0 && <Heading level={2} text={t("RELAY SELECTOR")} />}
        <Stack width={"100%"}>
            <Stepper
                activeStep={activeStep}
                handleStep={onStepChanged}
            >
                <Step title={t("1. Start")}>
                    {withFooter(<StartStep></StartStep>)}
                </Step>
                <Step title={productSelection.segment ? `2. ${productSelection.segment.name}` : t("2. Segment")}>
                    {withFooter(<SegmentStep segments={segments} subSegments={subSegments} selectedSubSegment={productSelection.segment} onSegmentSelected={onSegmentSelected}></SegmentStep>)}
                </Step>
                <Step title={productSelection.application ? `3. ${productSelection.application.name}` : t("3. Application")}>
                    {withFooter(<ApplicationStep applications={applications} selectedApplication={productSelection.application} onApplicationSelected={onApplicationSelected}></ApplicationStep>)}
                </Step>
                <Step title={t("4. Features")}>
                    {withFooter(<FeaturesStep key={activeStep}></FeaturesStep>)}
                </Step>
                <Step title={t("5. Comparison")}>
                    {withFooter(<ComparisonStep key={activeStep}></ComparisonStep>)}
                </Step>
                <Step title={t("6. Recommendation")}>
                    {withFooter(<RecommendedStep></RecommendedStep>)}
                </Step>
            </Stepper>
        </Stack>
    </AppContentWrapper>
}

export default ProductsView;
